/**
 * RCM (revenue cycle management) core: charge capture and patient payments (`BACKLOG.md` R1, R6,
 * R7). Deliberately narrow — see `server/src/db/schema/rcm.ts`'s header for what this does and does
 * not cover; the payer-facing half (claims, ERA, eligibility — R2-R5/R9) is unbuilt and nothing here
 * assumes it exists.
 *
 * `R7`'s "pure tested modules in shared/" requirement, same pattern as `careTeam.ts`/`authorization.ts`
 * `decide()`: the fee lookup and balance arithmetic are pure functions here, unit-testable with no
 * database, imported by both the server (to price a charge at capture time) and the web app (to
 * render a fee estimate before submitting).
 *
 * **The CPT/ICD-10 code sets below are a small curated demo catalog, not a licensed code database.**
 * CPT is AMA copyrighted and a real code set needs a paid license; ICD-10-CM needs no license but the
 * full set is ~70,000 codes. The same call `C3a`'s own note already made for drug data (First
 * Databank/Medi-Span licensing is a cost/contract item, not something to fake) applies here — this is
 * enough breadth to demonstrate charge capture against the specialties this demo already has patients
 * in (general/primary care, dermatology, aesthetics), not a production coding surface.
 */

import { z } from 'zod';

// --- Demo code catalogs -------------------------------------------------------------------------

export interface DemoCptCode {
  readonly code: string;
  readonly description: string;
  /** The demo fee schedule's price for one unit, in cents — never a float, matching every other
   * money value in this codebase (`aestheticsRequests.ts`'s `priceCents`/`totalPriceCents`). */
  readonly feeCents: number;
}

/** Ordered roughly by how often a general/derm/aesthetics demo encounter would use them. */
export const DEMO_CPT_CODES: readonly DemoCptCode[] = [
  {
    code: '99213',
    description: 'Office visit, established patient, low complexity',
    feeCents: 11000,
  },
  {
    code: '99214',
    description: 'Office visit, established patient, moderate complexity',
    feeCents: 16000,
  },
  { code: '99203', description: 'Office visit, new patient, low complexity', feeCents: 15000 },
  { code: '99204', description: 'Office visit, new patient, moderate complexity', feeCents: 22000 },
  { code: '11100', description: 'Biopsy of skin lesion, single', feeCents: 12000 },
  { code: '11101', description: 'Biopsy of skin lesion, each additional', feeCents: 4500 },
  { code: '17000', description: 'Destruction of premalignant lesion, first', feeCents: 9500 },
  {
    code: '96372',
    description: 'Therapeutic injection, subcutaneous or intramuscular',
    feeCents: 3500,
  },
  { code: '90471', description: 'Immunization administration, one vaccine', feeCents: 2800 },
] as const;

export const DEMO_CPT_CODE_VALUES = DEMO_CPT_CODES.map((c) => c.code) as [string, ...string[]];

export interface DemoIcd10Code {
  readonly code: string;
  readonly description: string;
}

export const DEMO_ICD10_CODES: readonly DemoIcd10Code[] = [
  {
    code: 'Z00.00',
    description: 'Encounter for general adult medical exam without abnormal findings',
  },
  { code: 'I10', description: 'Essential (primary) hypertension' },
  { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  { code: 'L70.0', description: 'Acne vulgaris' },
  { code: 'L57.0', description: 'Actinic keratosis' },
  { code: 'L82.1', description: 'Other seborrheic keratosis' },
  { code: 'J06.9', description: 'Acute upper respiratory infection, unspecified' },
  { code: 'M54.5', description: 'Low back pain' },
] as const;

export const DEMO_ICD10_CODE_VALUES = DEMO_ICD10_CODES.map((c) => c.code) as [string, ...string[]];

/** Returns `undefined` for a code not in the demo catalog rather than throwing — the route layer
 * uses this to return a typed `invalid_request` error rather than a 500; the web app uses it to show
 * a code's description next to its charge line. */
export const findCptCode = (cptCode: string): DemoCptCode | undefined =>
  DEMO_CPT_CODES.find((c) => c.code === cptCode);

/** Same shape as `findCptCode`, for the ICD-10 side of the demo catalog. */
export const findIcd10Code = (icd10Code: string): DemoIcd10Code | undefined =>
  DEMO_ICD10_CODES.find((c) => c.code === icd10Code);

/** The demo fee schedule's price for `units` of `cptCode`, in cents. Returns `undefined` for a code
 * not in the demo catalog rather than throwing — matches `findCptCode`'s own non-throwing shape, so
 * the caller decides how to turn "unknown code" into a response. */
export const lookUpFeeCents = (cptCode: string, units: number): number | undefined => {
  const found = findCptCode(cptCode);
  return found === undefined ? undefined : found.feeCents * units;
};

// --- Charge-line derivation, and the blocking-vs-warning contract (`BACKLOG.md` M3a, items 1-2) ---
//
// Ported from `emrx`'s `src/mock/charges.ts` (`docs/comparison-emrx.md` §5.1's first row): a charge
// line generated from a clinical source should carry a `derivedFrom` reference back to that source,
// and a data-quality problem on a line should default to a **blocking** error for a genuinely invalid
// state but a **warning** for a merely-questionable one — alert-fatigue reasoning applied to claim
// scrubbing, not every possible issue deserving the same severity. `chargesService.ts`'s `createCharge`
// decides `charges.status` from this at write time (`chargeStatusForIssues`); `listCharges`/`createCharge`
// both recompute `derivedFrom`/`errors`/`warnings` from the stored row rather than persisting them, so
// there is exactly one place this logic lives and a fix here reaches every already-stored charge too.

/** One entry in a charge line's "show your working" audit trail — what documented value the line was
 * derived from. Today this codebase has exactly one real source per charge (the encounter it was
 * captured against; there is no lesion-count/measurement-extraction pipeline like emrx's to derive a
 * code from), so the array has one entry — kept as an array, not a bare string, so a future source
 * (a specific procedure, a documented measurement) is additive rather than a shape change. */
export interface ChargeDerivationEntry {
  readonly label: string;
  readonly value: string;
}

/** What a charge line was derived from — see `ChargeDerivationEntry`'s own comment on why this is
 * one entry today. Pure and re-derivable from the encounter reference already stored on the row, so
 * nothing here is persisted separately. */
export const deriveChargeLineSource = (input: {
  readonly encounterId: string;
}): ChargeDerivationEntry[] => [{ label: 'Encounter', value: input.encounterId }];

/** CPT codes for an office visit (evaluation & management) — billed once per encounter in the
 * ordinary case. More than one unit is not invalid (a genuinely prolonged or split visit exists),
 * only unusual enough to be worth a second look before submission — a warning, not a block. */
const EVALUATION_MANAGEMENT_CPT_CODES: ReadonlySet<string> = new Set([
  '99213',
  '99214',
  '99203',
  '99204',
]);

/**
 * The blocking-vs-warning distinction itself. Pure over already-validated request fields — the
 * schema has already refused an unknown `cptCode`/`icd10Code`, an out-of-range `units`, or a missing
 * `encounterId`, so this only judges questions the schema cannot: is the diagnosis situation
 * consistent with how the charge is billed, and is the unit count plausible for the code.
 *
 * **Blocking (`errors`)**: no diagnosis pointer on a charge billed to a payer — a claim without one
 * cannot be submitted, matching emrx's `charges.ts` `"No diagnosis pointer..."` error exactly.
 *
 * **Warning, not blocking (`warnings`)**: no diagnosis pointer on a `selfPay` charge — a cosmetic or
 * cash-pay service legitimately has no billable diagnosis (`docs/comparison-emrx.md` §5.1's
 * "self-pay is a warning, not an error" row, `NOTES.md:534-539`'s "would have trained the biller to
 * ignore the error list" reasoning) — and more than one unit on an evaluation & management code,
 * which is unusual but not wrong.
 */
export const deriveChargeLineIssues = (input: {
  readonly cptCode: string;
  readonly units: number;
  readonly icd10Codes: readonly string[];
  readonly selfPay: boolean;
}): { readonly errors: string[]; readonly warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (input.icd10Codes.length === 0) {
    if (input.selfPay) {
      warnings.push(
        'No diagnosis on file — self-pay charge. Route to patient responsibility, not a payer.',
      );
    } else {
      errors.push(
        'No diagnosis pointer. A charge line must name the ICD-10 code that justifies it.',
      );
    }
  }

  if (input.units > 1 && EVALUATION_MANAGEMENT_CPT_CODES.has(input.cptCode)) {
    warnings.push(
      'Multiple units on an evaluation & management code is unusual — confirm before submitting.',
    );
  }

  return { errors, warnings };
};

/**
 * `charges.status` a newly-created line starts at, decided from `deriveChargeLineIssues`'s `errors`
 * alone — a warning never blocks. A blocking line starts `draft`: it exists (visible, correctable —
 * matching emrx's own choice to still generate a flagged line rather than refuse the request
 * outright) but is excluded from `computeStatementBalanceCents` until a biller resolves the problem
 * and it is promoted, the same way any other `draft` charge is not yet a bill. A line with only
 * warnings (or none) starts `ready`, matching every charge's behaviour before this contract existed.
 */
export const chargeStatusForIssues = (errors: readonly string[]): 'draft' | 'ready' =>
  errors.length > 0 ? 'draft' : 'ready';

/**
 * A patient's outstanding self-pay balance: the sum of their `ready`/`billed` charges minus the sum
 * of their `succeeded` payments. Pure arithmetic over already-loaded rows — the route/service layer
 * is what actually queries them, scoped by org and patient the same way every other chart query is.
 *
 * `void`-status charges are excluded (never billed); `draft` charges are excluded too — a charge is
 * not yet a bill until it is `ready`, matching `clinicalNotes.status`'s own draft-vs-signed split.
 * A `pending`/`failed` payment does not reduce the balance — only a `succeeded` one has actually
 * moved money.
 */
export const computeStatementBalanceCents = (
  charges: readonly { status: string; chargeAmountCents: number }[],
  payments: readonly { status: string; amountCents: number }[],
): number => {
  const billed = charges
    .filter((c) => c.status === 'ready' || c.status === 'billed')
    .reduce((sum, c) => sum + c.chargeAmountCents, 0);
  const paid = payments
    .filter((p) => p.status === 'succeeded')
    .reduce((sum, p) => sum + p.amountCents, 0);
  return billed - paid;
};

// --- Request/response schemas --------------------------------------------------------------------
//
// Same reasoning as every other `*Requests.ts` module: strict objects, no server-owned value
// (id/orgId/patientId/enteredByUserId/timestamps) accepted from the caller, request-side enums
// narrow, response-side fields plain strings/numbers reflecting what was actually stored.

export const RCM_ERROR_CODES = [
  /** The body failed schema validation, or `cptCode` is not in the demo catalog. */
  'invalid_request',
  /** The role holds the capability in general, but `evaluateChartAccess` refused this patient. */
  'chart_access_denied',
  /** The role does not hold the capability this route requires at all. */
  'forbidden',
  /** No such charge, payment, encounter, or patient in the caller's own tenant. */
  'not_found',
  /** The (stubbed or real) payment gateway declined the charge — see `gatewayRef`/`status`. */
  'payment_declined',
] as const;
export type RcmErrorCode = (typeof RCM_ERROR_CODES)[number];

const cptCodeSchema = z.enum(DEMO_CPT_CODE_VALUES);
const icd10CodeSchema = z.enum(DEMO_ICD10_CODE_VALUES);

/**
 * `icd10Codes` is `.min(0)` — genuinely optional — not `.min(1)` as it was before the
 * blocking-vs-warning contract above existed. A missing diagnosis is no longer refused at the
 * schema boundary; whether it is worth blocking on is `deriveChargeLineIssues`'s decision, made
 * from `selfPay` below. `selfPay` is caller-asserted, matching `confidentialityReasons`'s own
 * "explicit assertion, not inferred" shape — this codebase has no payer/coverage table to check
 * insurance-on-file against (`db/schema/rcm.ts`'s own header: "self-pay only... no payer-side
 * claims"), so the biller states self-pay explicitly at capture time rather than the system
 * guessing it from data that does not exist yet.
 */
export const createChargeRequestSchema = z
  .object({
    encounterId: z.uuid(),
    cptCode: cptCodeSchema,
    icd10Codes: z.array(icd10CodeSchema).min(0).max(12).default([]),
    units: z.number().int().min(1).max(99).default(1),
    selfPay: z.boolean().default(false),
    confidentialityReasons: z.array(z.string()).default([]),
  })
  .strict();
export type CreateChargeRequest = z.infer<typeof createChargeRequestSchema>;

const chargeDerivationEntrySchema = z.object({ label: z.string(), value: z.string() });

export const chargeResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid(),
  enteredByUserId: z.uuid(),
  cptCode: z.string(),
  icd10Codes: z.array(z.string()),
  units: z.number(),
  chargeAmountCents: z.number(),
  status: z.string(),
  selfPay: z.boolean(),
  /** "Show your working" — what documented value(s) this line was derived from. See
   * `deriveChargeLineSource`'s own comment. */
  derivedFrom: z.array(chargeDerivationEntrySchema),
  /** Blocking. A line with any entry here starts, and stays, `draft` until resolved — see
   * `chargeStatusForIssues`. */
  errors: z.array(z.string()),
  /** Non-blocking — worth a look before submission, never prevents one. */
  warnings: z.array(z.string()),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type ChargeResponse = z.infer<typeof chargeResponseSchema>;

export const chargeEnvelopeSchema = z.object({ charge: chargeResponseSchema });
export const chargeListEnvelopeSchema = z.object({ charges: z.array(chargeResponseSchema) });

/** `method` is open-but-checked free text today (`'card'` is the only value the stub gateway
 * produces) — narrowed to a real enum once a second real method exists, matching `orgs.tier`'s own
 * open-value-set precedent rather than a Postgres enum needing a migration to extend later. */
export const createPaymentRequestSchema = z
  .object({
    encounterId: z.uuid().nullable().default(null),
    amountCents: z.number().int().min(1),
    method: z.string().trim().min(1).max(50).default('card'),
  })
  .strict();
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>;

export const paymentResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  recordedByUserId: z.uuid(),
  amountCents: z.number(),
  method: z.string(),
  status: z.string(),
  gatewayRef: z.string().nullable(),
  processedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PaymentResponse = z.infer<typeof paymentResponseSchema>;

export const paymentEnvelopeSchema = z.object({ payment: paymentResponseSchema });
export const paymentListEnvelopeSchema = z.object({ payments: z.array(paymentResponseSchema) });

export const statementResponseSchema = z.object({
  patientId: z.uuid(),
  balanceCents: z.number(),
  charges: z.array(chargeResponseSchema),
  payments: z.array(paymentResponseSchema),
});
export type StatementResponse = z.infer<typeof statementResponseSchema>;

export const statementEnvelopeSchema = z.object({ statement: statementResponseSchema });

/**
 * Roll-up billing across a reseller's managed children (`BACKLOG.md` T17). One row per reachable
 * org — the caller's own org plus every direct child `session.reachableOrgIds` resolved
 * (`server/src/auth/middleware.ts`'s `resolveReachableOrgIds`, T14/T15/T16) — never a single flattened
 * sum, matching that row's own requirement: "a reseller needs to see which practice owes what".
 *
 * Deliberately org-level totals only, no per-charge or per-patient detail: `billedCents`/`paidCents`/
 * `balanceCents` are `@emr/shared`'s own `computeStatementBalanceCents` arithmetic aggregated across
 * one org's charges/payments, never a diagnosis code, a CPT code, or a patient id. `reseller_admin`
 * (T16) holds `patient:read` and `org:read_children`, never `chart:read` — this response shape is
 * what keeps that true: there is nothing here a `chart:read` capability would be needed to see.
 */
export const rollupOrgStatementSchema = z.object({
  orgId: z.uuid(),
  orgName: z.string(),
  /** True for the caller's own org, false for a reachable child — lets the UI visually separate
   * "your own practice" from "a managed practice" without a second round trip. */
  isOwnOrg: z.boolean(),
  billedCents: z.number(),
  paidCents: z.number(),
  balanceCents: z.number(),
});
export type RollupOrgStatement = z.infer<typeof rollupOrgStatementSchema>;

export const rollupStatementResponseSchema = z.object({
  orgs: z.array(rollupOrgStatementSchema),
  totalBilledCents: z.number(),
  totalPaidCents: z.number(),
  totalBalanceCents: z.number(),
});
export type RollupStatementResponse = z.infer<typeof rollupStatementResponseSchema>;

export const rollupStatementEnvelopeSchema = z.object({
  rollupStatement: rollupStatementResponseSchema,
});
